import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';
import { APIProvider, AdvancedMarker, Circle, InfoWindow, Map, Pin, useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { buildMapMarkers } from './mapMarkers';
import { mapsApiKey, mapsMapId } from './mapsConfig';

const SWEDEN_CENTER = { lat: 62.5, lng: 16 };
const SWEDEN_ZOOM = 4;
const SINGLE_POINT_ZOOM = 10;
const KM_PER_DEGREE_LAT = 111.045;

const defaultTexts = {
  unmappedAds: (count) => (count === 1
    ? "1 job ad has no exact location and isn't shown on the map"
    : `${count} job ads have no exact location and aren't shown on the map`),
  noLocations: 'None of these job ads have a location to show on the map',
};

const FitBounds = ({ points, searchArea }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    if (searchArea) {
      const latDelta = searchArea.radius_km / KM_PER_DEGREE_LAT;
      const lngDelta = searchArea.radius_km / (KM_PER_DEGREE_LAT * Math.max(Math.cos((searchArea.lat * Math.PI) / 180), 0.01));
      bounds.extend({ lat: searchArea.lat - latDelta, lng: searchArea.lng - lngDelta });
      bounds.extend({ lat: searchArea.lat + latDelta, lng: searchArea.lng + lngDelta });
    }

    if (bounds.isEmpty()) {
      map.setCenter(SWEDEN_CENTER);
      map.setZoom(SWEDEN_ZOOM);
    } else if (points.length === 1 && !searchArea?.radius_km) {
      map.setCenter(points[0]);
      map.setZoom(SINGLE_POINT_ZOOM);
    } else {
      map.fitBounds(bounds, 48);
    }
  }, [map, points, searchArea]);

  return null;
};

const ClusteredPins = ({ pins, onSelect }) => {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const clusterer = useMemo(() => (map ? new MarkerClusterer({ map }) : null), [map]);

  useEffect(() => {
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  useEffect(() => () => clusterer?.setMap(null), [clusterer]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((current) => {
      if ((marker && current[key]) || (!marker && !current[key])) return current;
      if (marker) return { ...current, [key]: marker };
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  return pins.map((pin) => (
    <PinMarker key={pin.key} pin={pin} setMarkerRef={setMarkerRef} onSelect={onSelect} />
  ));
};

const PinMarker = ({ pin, setMarkerRef, onSelect }) => {
  const { key } = pin;
  const ref = useCallback((marker) => setMarkerRef(marker, key), [setMarkerRef, key]);
  const handleClick = useCallback(() => onSelect(pin), [onSelect, pin]);

  return (
    <AdvancedMarker position={pin.position} title={pin.name} ref={ref} onClick={handleClick}>
      <Pin />
    </AdvancedMarker>
  );
};

const SelectedAds = ({ selected, getAdUrl }) => (
  <Stack gap={1} maxWidth={260}>
    <Typography variant="caption" fontWeight={600} color="text.secondary">
      {selected.name}
    </Typography>
    {selected.ads.map((ad) => (
      <Stack key={ad.ad_id} gap={0.25}>
        <Link component={RouterLink} to={getAdUrl(ad)} fontWeight={600} underline="hover">
          {ad.headline}
        </Link>
        {ad.buyer_name && (
          <Typography variant="caption" color="text.secondary">
            {ad.buyer_name}
          </Typography>
        )}
      </Stack>
    ))}
  </Stack>
);

export const JobAdsMap = ({ jobAds, searchArea, getAdUrl, lang = 'en', texts: customTexts = {} }) => {
  const texts = { ...defaultTexts, ...customTexts };
  const theme = useTheme();
  const [selected, setSelected] = useState(null);
  const { pins, unmappedAds } = useMemo(() => buildMapMarkers(jobAds), [jobAds]);
  const points = useMemo(() => pins.map((pin) => pin.position), [pins]);
  const isEmpty = points.length === 0;

  return (
    <Stack height={1} gap={1}>
      {(unmappedAds > 0 || isEmpty) && (
        <Typography variant="body2" color="text.secondary">
          {isEmpty ? texts.noLocations : texts.unmappedAds(unmappedAds)}
        </Typography>
      )}
      <Box flex={1} minHeight={320} borderRadius={1} overflow="hidden">
        <APIProvider apiKey={mapsApiKey} language={lang}>
          <Map
            mapId={mapsMapId}
            defaultCenter={SWEDEN_CENTER}
            defaultZoom={SWEDEN_ZOOM}
            gestureHandling="greedy"
            streetViewControl={false}
            mapTypeControl={false}
            fullscreenControl={false}
            style={{ width: '100%', height: '100%' }}
          >
            <FitBounds points={points} searchArea={searchArea} />
            {searchArea?.radius_km > 0 && (
              <Circle
                center={{ lat: searchArea.lat, lng: searchArea.lng }}
                radius={searchArea.radius_km * 1000}
                strokeColor={theme.palette.primary.main}
                strokeOpacity={0.8}
                strokeWeight={2}
                fillColor={theme.palette.primary.main}
                fillOpacity={0.08}
                clickable={false}
              />
            )}
            <ClusteredPins pins={pins} onSelect={setSelected} />
            {selected && (
              <InfoWindow position={selected.position} pixelOffset={[0, -36]} onCloseClick={() => setSelected(null)}>
                <SelectedAds selected={selected} getAdUrl={getAdUrl} />
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </Box>
    </Stack>
  );
};
